import React, { useEffect, useRef, useState } from 'react';
import Overlay from './components/common/Overlay';
import {
  EmptyProductData,
  ProductData,
  ProductMetaData,
  ProductVariant,
  TransitionalVideo,
  WishlistItemProps,
} from './interfaces/components';
import Instructions from './pages/Instructions';
import TransitionVideo from './pages/TransitionVideo';
import ExampleTransitionVideo from './assets/videos/Intro.mp4';
import PDP from './pages/PDP';
import {
  dispatchAudioAnalytics,
  dispatchCollectItemAnalytics,
  dispatchStartTagManagerAnalytics,
  dispatchCTAClickObject,
  dispatchAddToCartAnalytics,
} from './Analytics';
import AudioToggleModal from './pages/AudioToggleModal';
import backgroundAudio from './assets/audios/background.mp3';
import Welcome from './pages/Welcome';
import Subscribe from './pages/Subscribe';
import RefillFountain from './pages/RefillFountain';
import WhatToBring from './pages/WhatToBring';
import RetroHoliday from './pages/RetroHoliday';
import ChristmasPick from './pages/ChristmasPick';
import ArtOfGift from './pages/ArtOfGift';
import RewardInfo from './pages/RewardInfo';
import PostCardSent from './pages/PostCardSent';
import PostCard from './pages/PostCard';
import PostCardMobile from './pages/PostCardMobile';
import Spinner from './components/common/Spinner';
import axios from 'axios';
import OutOfStock from './pages/OutOfStock';
import {
  getBrowserName,
  getOpSys,
  getParameter,
  mergeArraysUnique,
  undoLoccitaneLocale,
  findNonCommonItems,
} from './utils/helper';
import { ArabicIngredients, PDPContent } from './content';
import Toast from './components/common/Toast';
import Wishlist from './pages/Wishlist';
import Completed from './pages/Completed';
import useWindowDimensions from './utils/hooks/useWindowDimensions';
import AddedToCart from './pages/AddedToCart';
import { v4 as uuidv4 } from 'uuid';

function App() {
  //Refs
  const audioRef = useRef<HTMLAudioElement>(null);

  // Hooks
  const { width } = useWindowDimensions();

  //States
  const [activeLocale, setActiveLocale] = useState('');
  const [activeScene, setActiveScene] = useState('room-1');
  const [audioActive, setAudioActive] = useState(false);
  const [audioModal, setAudioModal] = useState(true);
  const [tabVisible, setTabVisible] = useState<boolean>(true);
  const [welcomeActive, setWelcomeActive] = useState(false);
  const [wishlistActive, setWishlistActive] = useState(false);
  const [instructionsActive, setInstructionsActive] = useState(false);
  const [subscribeActive, setSubscribeActive] = useState(false);
  const [refillFountainActive, setRefillFountainActive] = useState(false);
  const [whatToBringActive, setWhatToBringActive] = useState(false);
  const [retroHolidayActive, setRetroHolidayActive] = useState(false);
  const [christmasPickActive, setChristmasPickActive] = useState(false);
  const [artOfGiftActive, setArtOfGiftActive] = useState(false);
  const [rewardInfoActive, setRewardInfoActive] = useState(false);
  const [postCardActive, setPostCardActive] = useState(false);
  const [postCardMobileActive, setPostCardMobileActive] = useState(false);
  const [postCardSentActive, setPostCardSentActive] = useState(false);
  const [addedToCartActive, setAddedToCartActive] = useState(false);
  const [miniGameActive, setMiniGameActive] = useState(false);
  const [wishlistProducts, setWishlistProducts] = useState([]);

  const [collectiblesCollectedCount, setCollectiblesCollectedCount] = useState(0);
  const [collectibleCollectedTrack, setCollectibleCollectedTrack] = useState({
    giftcard: false,
    ribbon: false,
    giftbox: false,
  });

  const [toastActive, setToastActive] = useState({ active: false, text: '' });
  const [wishListToastActive, setWishlistToastActive] = useState({ active: false, text: '' });
  const [collectibletToastActive, setCollectibletToastActive] = useState(false);

  const [oosActive, setOosActive] = useState(false);
  const [pdpLoading, setPdpLoading] = useState(false);
  const [pdpActive, setPDPActive] = useState(false);
  const [pdpData, setPDPData] = useState<ProductData>();
  const [productMetaData, setProductMetaData] = useState<ProductMetaData>();

  const [transitionVideo, setTransitionVideo] = useState<TransitionalVideo>({
    videoURL: ExampleTransitionVideo,
    active: false,
    gotoRoom: 'room-1',
  });

  const fetchProductById = async (productId: string, apiLocale: string) => {
    try {
      const response = await axios.get(process.env.REACT_APP_API_URL + `${apiLocale}/` + productId, {
        params: {
          'include-data': false,
        },
      });
      return response.data;
    } catch (error: unknown) {
      console.error(`Error fetching product with ID ${productId}`);
      throw error;
    }
  };

  const fetchProducts = async (productIds: string[], apiLocale: string) => {
    const products = [];

    for (const productId of productIds) {
      try {
        const product = await fetchProductById(productId, apiLocale);
        products.push(product.data);
      } catch (error) {
        console.log('Error in fetchProducts');
      }
    }
    return products;
  };

  useEffect(() => {
    const iframeUrlConsistsIds = window.location.href.includes('ids=');
    const parentUrlConsistsIds = document.referrer.includes('ids=');

    if (iframeUrlConsistsIds || parentUrlConsistsIds) {
      dispatchCTAClickObject('loccitane', 'button', 'enterVirtualStore', 'open', document.referrer);
    }

    const ids = getParameter(window.location.href, 'ids') || '';
    const locale = undoLoccitaneLocale(getParameter(window.location.href, 'lang') || '');
    const sessionIdExists = localStorage.getItem('sessionId') || '';
    const prevIds = localStorage.getItem('wishlistIds') || '';

    if (sessionIdExists?.length > 0 && prevIds.length > 0) {
      console.log('Session Id Exists, compare ids only');

      const prevIdsArray = prevIds.split(',');
      const newIdsArray = ids?.split(',');

      const nonCommonIdsArray = findNonCommonItems(prevIdsArray, newIdsArray);
      const allIdsArray = mergeArraysUnique(prevIdsArray, newIdsArray);

      console.log('nonCommonIdsArray', nonCommonIdsArray);
      localStorage.setItem('wishlistIds', allIdsArray.toString());

      if (nonCommonIdsArray?.length > 0 && locale?.length > 0) {
        setToastActive({ active: true, text: 'Adding Wishlist To Cart' });

        fetchProducts(nonCommonIdsArray, locale)
          .then((products) => {
            console.log(products);
            products.forEach((product: ProductData) => {
              const variantData = product?.variants.find((variant: ProductVariant) => variant.default);

              const analyticItem = {
                item_id: variantData?.variant_id ? variantData?.variant_id : '',
                item_name: product?.title ? product?.title : '',
                affiliation: '',
                coupon: '',
                discount: 0,
                currency: product?.currency ? product?.currency : '',
                index: 0,
                item_brand: "L'Occitane",
                item_category: variantData?.variants[0]?.value ? variantData?.variants[0]?.value : '',
                item_category2: product?.category ? product?.category : '',
                item_category3: product?.collection ? product.collection : '',
                item_category4: '',
                item_category5: variantData?.retail_price ? variantData?.retail_price : '', // should also be price
                item_list_id: '',
                item_list_name: '',
                item_variant: variantData?.variant_id ? variantData?.variant_id : '',
                location_id: '',
                price: parseInt(variantData?.retail_price ? variantData?.retail_price : ''),
                quantity: 1,
              };

              const eventMessage = {
                event_id: 'add_to_cart',
                product_id: variantData?.variant_id ? variantData?.variant_id : '',
                color_id: '',
                title: product?.title ? product?.title : '',
                price: product?.base_price,
                size_mpn: '',
                gtin: '',
              };

              console.log(eventMessage);
              window.parent.postMessage(eventMessage, '*');

              dispatchAddToCartAnalytics(
                product?.currency ? product?.currency : 'AED',
                variantData?.retail_price ? variantData?.retail_price : '',
                [analyticItem],
                'loccitane',
              );
            });
            setToastActive({ ...toastActive, active: false });
            setAddedToCartActive(true);
          })
          .catch((error) => {
            console.error('Error fetching products:', error);
          });
      }
    } else {
      console.log('Session Id Does Not Exists, add ids');

      const newMockSessionId = uuidv4();
      localStorage.setItem('sessionId', newMockSessionId);
      localStorage.setItem('wishlistIds', ids);

      if (ids?.length > 0 && locale?.length > 0) {
        console.log(ids?.split(','));

        setToastActive({ active: true, text: 'Adding Wishlist To Cart' });

        fetchProducts(ids?.split(','), locale)
          .then((products) => {
            console.log(products);
            products.forEach((product: ProductData) => {
              const variantData = product?.variants.find((variant: ProductVariant) => variant.default);

              const analyticItem = {
                item_id: variantData?.variant_id ? variantData?.variant_id : '',
                item_name: product?.title ? product?.title : '',
                affiliation: '',
                coupon: '',
                discount: 0,
                currency: product?.currency ? product?.currency : '',
                index: 0,
                item_brand: "L'Occitane",
                item_category: variantData?.variants[0]?.value ? variantData?.variants[0]?.value : '',
                item_category2: product?.category ? product?.category : '',
                item_category3: product?.collection ? product.collection : '',
                item_category4: '',
                item_category5: variantData?.retail_price ? variantData?.retail_price : '', // should also be price
                item_list_id: '',
                item_list_name: '',
                item_variant: variantData?.variant_id ? variantData?.variant_id : '',
                location_id: '',
                price: parseInt(variantData?.retail_price ? variantData?.retail_price : ''),
                quantity: 1,
              };

              const eventMessage = {
                event_id: 'add_to_cart',
                product_id: variantData?.variant_id ? variantData?.variant_id : '',
                color_id: '',
                title: product?.title ? product?.title : '',
                price: product?.base_price,
                size_mpn: '',
                gtin: '',
              };

              console.log(eventMessage);
              window.parent.postMessage(eventMessage, '*');

              dispatchAddToCartAnalytics(
                product?.currency ? product?.currency : 'AED',
                variantData?.retail_price ? variantData?.retail_price : '',
                [analyticItem],
                'loccitane',
              );
            });
            setToastActive({ ...toastActive, active: false });
            setAddedToCartActive(true);
          })
          .catch((error) => {
            console.error('Error fetching products:', error);
          });
      }
    }
  }, []);

  useEffect(() => {
    const giftcard = localStorage.getItem('giftcard') === 'collected' ? true : false;
    const ribbon = localStorage.getItem('ribbon') === 'collected' ? true : false;
    const giftbox = localStorage.getItem('giftbox') === 'collected' ? true : false;

    setCollectibleCollectedTrack({
      giftcard: giftcard,
      ribbon: ribbon,
      giftbox: giftbox,
    });

    const totalCollectedCount = (giftcard ? 1 : 0) + (ribbon ? 1 : 0) + (giftbox ? 1 : 0);

    setCollectiblesCollectedCount(totalCollectedCount);
  }, []);

  const collectibleCollected = (collectible: string) => {
    if (collectiblesCollectedCount <= 3) {
      setCollectibletToastActive(true);
      setCollectiblesCollectedCount((count) => count + 1);
    }

    const collectedCollectibles = parseInt(localStorage.getItem('collectedCollectibles') || '0');

    dispatchCollectItemAnalytics('minigame', (collectedCollectibles + 1).toString(), 'loccitane');
    if (collectedCollectibles + 1 < 3) {
      setRewardInfoActive(true);
    } else {
      setMiniGameActive(true);
    }

    setCollectibleCollectedTrack((prevState) => ({ ...prevState, [collectible]: true }));
    localStorage.setItem(collectible, 'collected');
  };

  useEffect(() => {
    const locale = window.locale;
    if (locale === 'ar-ae' || locale === 'ar-sa' || locale === 'ar-kw' || locale === 'ar-qa') {
      setActiveLocale('ar');
    } else if (locale === 'en-ae' || locale === 'en-sa' || locale === 'en-kw' || locale === 'en-qa') {
      setActiveLocale('en');
    }
  }, []);

  useEffect(() => {
    if (activeLocale.includes('ar')) {
      const wishlistProducts = localStorage.getItem('wishlistProductsAR');
      if (wishlistProducts && JSON.parse(wishlistProducts).length > 0) {
        const fetchedWishlistProducts = JSON.parse(wishlistProducts);
        setWishlistProducts(fetchedWishlistProducts);
      } else {
        setWishlistProducts([]);
      }
    } else if (activeLocale.includes('en')) {
      const wishlistProducts = localStorage.getItem('wishlistProducts');
      if (wishlistProducts && JSON.parse(wishlistProducts).length > 0) {
        const fetchedWishlistProducts = JSON.parse(wishlistProducts);
        setWishlistProducts(fetchedWishlistProducts);
      } else {
        setWishlistProducts([]);
      }
    }
  }, [pdpActive, activeLocale]);

  const playTransitionVideo = (videoName: string, room: string) => {
    const videoURLs = {
      videoOne: ExampleTransitionVideo,
      videoTwo: ExampleTransitionVideo,
      videoThree: ExampleTransitionVideo,
    };

    setTransitionVideo({
      active: true,
      videoURL: videoURLs[videoName as keyof typeof videoURLs],
      gotoRoom: room,
    });
  };

  const setProductMetaDetails = (roomName: string) => {
    const orgIdMetaTag = document.querySelector('meta[name=org_id]');
    const experienceNameMetaTag = document.querySelector('meta[name=experience_name]');
    let organisationName: string | null = '';
    let experienceName: string | null = '';
    if (orgIdMetaTag) {
      organisationName = orgIdMetaTag.getAttribute('content');
    }
    if (experienceNameMetaTag) {
      experienceName = experienceNameMetaTag.getAttribute('content');
    }
    setProductMetaData({
      organisationName: organisationName,
      experienceName: experienceName,
      roomName: roomName,
      browser: getBrowserName(),
      os: getOpSys(),
    });
  };

  const openProductPopUp = (productId: string, collection: string, scene: string) => {
    dispatchCTAClickObject('loccitane', 'button', 'productOpen', productId, '');
    console.log('Product', productId, collection);
    const roomName: string = scene.split('_')[0];
    setProductMetaDetails(roomName);
    let apiLocale = '';

    if (window.locale === 'ar-ae') {
      apiLocale = 'ar-ae';
    } else if (window.locale === 'ar-sa') {
      apiLocale = 'ar-sa';
    } else if (window.locale === 'en-sa') {
      apiLocale = 'en-sa';
    } else if (window.locale === 'en-kw') {
      apiLocale = 'en-kw';
    } else if (window.locale === 'ar-kw') {
      apiLocale = 'ar-kw';
    } else if (window.locale === 'en-qa') {
      apiLocale = 'en-qa';
    } else if (window.locale === 'ar-qa') {
      apiLocale = 'ar-qa';
    } else {
      apiLocale = 'en-ae';
    }

    if (productId !== null) {
      setPdpLoading(true);
      axios
        .get(process.env.REACT_APP_API_URL + `${apiLocale}/` + productId, {
          params: {
            'include-data': false,
          },
        })
        .then((res) => {
          console.log(res);

          const data = res.data.data as ProductData;

          if (
            window.locale === 'ar-ae' ||
            window.locale === 'ar-sa' ||
            window.locale === 'ar-qa' ||
            window.locale === 'ar-kw'
          ) {
            setPDPData({ ...data, collection: ArabicIngredients[collection as keyof typeof ArabicIngredients] });
          } else {
            setPDPData({ ...data, collection });
          }
        })
        .catch((err: unknown) => {
          console.log(err);
          setPdpLoading(false);
          setOosActive(true);
        });
    } else {
      setOosActive(true);
    }
  };

  useEffect(() => {
    if (pdpData && pdpData.title !== '') {
      setPdpLoading(false);
      setPDPActive(true);
    }
  }, [pdpData]);

  const openRefillFountain = () => {
    dispatchCTAClickObject('loccitane', 'button', 'refillFountail', 'open', '');
    setRefillFountainActive(true);
  };

  const openWhatToBring = () => {
    dispatchCTAClickObject('loccitane', 'button', 'whatToBring', 'open', '');
    setWhatToBringActive(true);
  };

  const openRetroHoliday = () => {
    dispatchCTAClickObject('loccitane', 'button', 'retroHoliday', 'open', '');
    setRetroHolidayActive(true);
  };

  const openChristmasPick = () => {
    dispatchCTAClickObject('loccitane', 'button', 'christmasPick', 'open', '');
    setChristmasPickActive(true);
  };

  const openPostCard = () => {
    dispatchCTAClickObject('loccitane', 'button', 'postCard', 'open', '');
    console.log(width);
    width < 992 ? setPostCardMobileActive(true) : setPostCardActive(true);
  };

  const openGameCompleted = () => {
    setMiniGameActive(true);
  };

  const openArtOfGift = () => {
    dispatchCTAClickObject('loccitane', 'button', 'artOfGift', 'open', '');
    setArtOfGiftActive(true);
  };

  const updateActiveScene = (sceneName: string) => {
    if (activeScene !== sceneName) {
      setActiveScene(sceneName);
    }
  };

  useEffect(() => {
    window.openArtOfGift = openArtOfGift;
    window.openGameCompleted = openGameCompleted;
    window.openPostCard = openPostCard;
    window.openChristmasPick = openChristmasPick;
    window.openRetroHoliday = openRetroHoliday;
    window.openWhatToBring = openWhatToBring;
    window.openRefillFountain = openRefillFountain;
    window.openProductPopUp = openProductPopUp;
    window.playTransitionVideo = playTransitionVideo;
    window.collectibleCollected = collectibleCollected;
    window.updateScene = updateActiveScene;
  }, []);

  // EventListeners
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') setTabVisible(true);
    else setTabVisible(false);
  });

  useEffect(() => {
    if (audioActive && !tabVisible) {
      if (audioRef.current) {
        dispatchAudioAnalytics(false, 'loccitane');
        audioRef.current.pause();
        setAudioActive(false);
      }
    }
  }, [tabVisible]);

  const toggleAudio = () => {
    if (!audioActive) {
      if (audioRef.current) {
        dispatchAudioAnalytics(true, 'loccitane');
        audioRef.current.play();
        setAudioActive(true);
      }
    } else {
      if (audioRef.current) {
        dispatchAudioAnalytics(false, 'loccitane');
        audioRef.current.pause();
        setAudioActive(false);
      }
    }
  };

  const playInitialAudio = (state: boolean) => {
    if (audioRef.current) audioRef.current.volume = 0.5;

    if (state) {
      if (audioRef.current) {
        audioRef.current.play();
        setAudioActive(true);
      }
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        setAudioActive(false);
      }
    }
  };

  const addToWishlist = (wishlistProduct: WishlistItemProps) => {
    const addedText =
      activeLocale === undefined || !((activeLocale as keyof typeof PDPContent) in PDPContent)
        ? PDPContent['en'].addedToWishlist
        : PDPContent[activeLocale as keyof typeof PDPContent].addedToWishlist;
    const alreadyAddedText =
      activeLocale === undefined || !((activeLocale as keyof typeof PDPContent) in PDPContent)
        ? PDPContent['en'].addedToWishlistAlready
        : PDPContent[activeLocale as keyof typeof PDPContent].addedToWishlistAlready;

    if (activeLocale === 'ar') {
      const wishlistProducts = localStorage.getItem('wishlistProductsAR');

      if (wishlistProducts && JSON.parse(wishlistProducts).length > 0) {
        const fetchedWishlistProducts = JSON.parse(wishlistProducts);
        const alreadyAdded = fetchedWishlistProducts.some(
          (fetchedWishlistProduct: WishlistItemProps) =>
            fetchedWishlistProduct.product_id === wishlistProduct.product_id,
        );
        if (!alreadyAdded) {
          const updatedWishlistProducts = [...fetchedWishlistProducts, wishlistProduct];
          localStorage.setItem('wishlistProductsAR', JSON.stringify(updatedWishlistProducts));

          setWishlistToastActive({
            active: true,
            text: addedText,
          });
        } else {
          setWishlistToastActive({
            active: true,
            text: alreadyAddedText,
          });
        }
      } else {
        const firstWishlistProduct = [wishlistProduct];
        localStorage.setItem('wishlistProductsAR', JSON.stringify(firstWishlistProduct));
        setWishlistToastActive({
          active: true,
          text: addedText,
        });
      }
    } else {
      const wishlistProducts = localStorage.getItem('wishlistProducts');

      if (wishlistProducts && JSON.parse(wishlistProducts).length > 0) {
        const fetchedWishlistProducts = JSON.parse(wishlistProducts);
        const alreadyAdded = fetchedWishlistProducts.some(
          (fetchedWishlistProduct: WishlistItemProps) =>
            fetchedWishlistProduct.product_id === wishlistProduct.product_id,
        );

        if (!alreadyAdded) {
          const updatedWishlistProducts = [...fetchedWishlistProducts, wishlistProduct];
          localStorage.setItem('wishlistProducts', JSON.stringify(updatedWishlistProducts));
          setWishlistToastActive({
            active: true,
            text: addedText,
          });
        } else {
          setWishlistToastActive({
            active: true,
            text: alreadyAddedText,
          });
        }
      } else {
        const firstWishlistProduct = [wishlistProduct];
        localStorage.setItem('wishlistProducts', JSON.stringify(firstWishlistProduct));
        setWishlistToastActive({
          active: true,
          text: addedText,
        });
      }
    }
  };

  const removeFromWishlist = (productId: string) => {
    if (activeLocale === 'ar') {
      const updatedWishlistProducts = wishlistProducts.filter(
        (product: WishlistItemProps) => product.product_id !== productId,
      );

      setWishlistProducts(updatedWishlistProducts);
      localStorage.setItem('wishlistProductsAR', JSON.stringify(updatedWishlistProducts));
    } else {
      const updatedWishlistProducts = wishlistProducts.filter(
        (product: WishlistItemProps) => product.product_id !== productId,
      );

      setWishlistProducts(updatedWishlistProducts);
      localStorage.setItem('wishlistProducts', JSON.stringify(updatedWishlistProducts));
    }
  };

  return (
    <>
      <AudioToggleModal
        activeLocale={activeLocale}
        proceedAudio={(state: boolean) => {
          dispatchAudioAnalytics(state, 'loccitane');
          playInitialAudio(state);
          setAudioModal(false);
          setWelcomeActive(true);
        }}
        active={audioModal}
      />
      <audio ref={audioRef} src={backgroundAudio} />

      <Toast
        activeLocale={activeLocale}
        position="absolute"
        active={toastActive.active}
        text={toastActive.text}
        setActive={(state: boolean) => setToastActive({ ...toastActive, active: state })}
      />

      <Wishlist
        wishlistProducts={wishlistProducts}
        activeLocale={activeLocale}
        active={wishlistActive}
        setActive={() => setWishlistActive(false)}
        removeFromWishlist={(productId: string) => removeFromWishlist(productId)}
      />

      <Overlay
        collectibletToastActive={collectibletToastActive}
        wishlistToastActive={wishListToastActive.active}
        wishlistToastText={wishListToastActive.text}
        collectedItems={collectibleCollectedTrack}
        audioActive={audioActive}
        toggleAudio={toggleAudio}
        activeLocale={activeLocale}
        closeAllUI={() => {
          setInstructionsActive(false);
        }}
        activeScene={activeScene}
        setActiveScene={setActiveScene}
        toggleInstructions={() => {
          const action = instructionsActive ? 'open' : 'close';
          dispatchCTAClickObject('loccitane', 'button', 'toggleInstructions', action, '');
          setInstructionsActive(!instructionsActive);
        }}
        setLocale={(locale) => {
          dispatchCTAClickObject('loccitane', 'button', 'togglLocale', locale, '');
          setActiveLocale(locale);

          if (locale === 'en') {
            if (window.originalLocale === 'en-ae') {
              window.locale = 'en-ae';
            } else if (window.originalLocale === 'en-sa') {
              window.locale = 'en-sa';
            } else if (window.originalLocale === 'en-kw') {
              window.locale = 'en-kw';
            } else if (window.originalLocale === 'en-qa') {
              window.locale = 'en-qa';
            } else if (window.originalLocale === 'ar-ae') {
              window.locale = 'en-ae';
            } else if (window.originalLocale === 'ar-sa') {
              window.locale = 'en-sa';
            } else if (window.originalLocale === 'ar-kw') {
              window.locale = 'en-kw';
            } else if (window.originalLocale === 'ar-qa') {
              window.locale = 'en-qa';
            }
          } else if (locale === 'ar') {
            console.log('Heere');
            console.log(window.originalLocale);

            if (window.originalLocale === 'en-ae') {
              window.locale = 'ar-ae';
            } else if (window.originalLocale === 'en-sa') {
              window.locale = 'ar-sa';
            } else if (window.originalLocale === 'en-kw') {
              window.locale = 'ar-kw';
            } else if (window.originalLocale === 'en-qa') {
              window.locale = 'ar-qa';
            } else if (window.originalLocale === 'ar-ae') {
              window.locale = 'ar-ae';
            } else if (window.originalLocale === 'ar-sa') {
              window.locale = 'ar-sa';
            } else if (window.originalLocale === 'ar-kw') {
              window.locale = 'ar-kw';
            } else if (window.originalLocale === 'ar-qa') {
              window.locale = 'ar-qa';
            }
          }
        }}
        setWishlistToastActive={(state: boolean) => setWishlistToastActive({ ...wishListToastActive, active: state })}
        setCollectibletToastActive={(state: boolean) => setCollectibletToastActive(state)}
        openWishlist={() => {
          dispatchCTAClickObject('loccitane', 'button', 'wishList', 'open', '');
          if (wishlistProducts.length > 0) {
            setWishlistActive(true);
          } else {
            setWishlistToastActive({
              active: true,
              text: activeLocale.includes('ar') ? 'قائمة أمنياتك فارغة' : 'Your wishlist is empty',
            });
          }
        }}
        openReward={() => {
          if (collectiblesCollectedCount === 3) {
            dispatchCTAClickObject('loccitane', 'button', 'minigameReward', 'open', '');
            setMiniGameActive(true);
          } else {
            dispatchCTAClickObject('loccitane', 'button', 'rewardInfo', 'open', '');
            setRewardInfoActive(true);
          }
        }}
      />

      {pdpLoading ? (
        <Spinner />
      ) : (
        <>
          <OutOfStock
            activeLocale={activeLocale}
            active={oosActive}
            setActive={(state: boolean) => setOosActive(state)}
          />
          <PDP
            addToWishlist={addToWishlist}
            setToastActive={(text: string) => setToastActive({ active: true, text: text })}
            activeLocale={activeLocale}
            pdpData={pdpData}
            active={pdpActive}
            productMetaData={productMetaData as ProductMetaData}
            setActive={(state) => {
              setPDPActive(state);
              setTimeout(() => {
                setPDPData(EmptyProductData);
              }, 500);
            }}
          />
        </>
      )}

      <Welcome
        activeLocale={activeLocale}
        active={welcomeActive}
        setActive={() => {
          dispatchStartTagManagerAnalytics('loccitane');
          dispatchCTAClickObject('loccitane', 'button', 'welcome', 'enter', '');
          setWelcomeActive(false);
          setInstructionsActive(true);
        }}
      />
      <Instructions
        activeLocale={activeLocale}
        active={instructionsActive}
        setActive={() => {
          setInstructionsActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'instructions', 'close', '');
        }}
      />
      <Subscribe activeLocale={activeLocale} active={subscribeActive} setActive={() => setSubscribeActive(false)} />
      <RefillFountain
        activeLocale={activeLocale}
        active={refillFountainActive}
        setActive={() => {
          dispatchCTAClickObject('loccitane', 'button', 'retroHoliday', 'close', '');
          setRefillFountainActive(false);
        }}
      />
      <WhatToBring
        activeLocale={activeLocale}
        active={whatToBringActive}
        setActive={() => {
          setWhatToBringActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'whatToBring', 'close', '');
        }}
      />
      <RetroHoliday
        activeLocale={activeLocale}
        active={retroHolidayActive}
        setActive={() => {
          setRetroHolidayActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'retroHoliday', 'close', '');
        }}
      />
      <ChristmasPick
        activeLocale={activeLocale}
        active={christmasPickActive}
        setActive={() => {
          setChristmasPickActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'christmasPick', 'close', '');
        }}
      />
      <ArtOfGift
        activeLocale={activeLocale}
        active={artOfGiftActive}
        setActive={() => {
          setArtOfGiftActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'artOfGift', 'close', '');
        }}
      />
      <RewardInfo
        leftItems={3 - collectiblesCollectedCount}
        collectedItems={collectibleCollectedTrack}
        activeLocale={activeLocale}
        active={rewardInfoActive}
        setActive={() => {
          setRewardInfoActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'rewardInfo', 'close', '');
        }}
      />
      <PostCard
        wishlistProducts={wishlistProducts}
        activeLocale={activeLocale}
        active={postCardActive}
        setActive={() => {
          setPostCardActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'postCard', 'close', '');
        }}
        removeFromWishlist={(productId: string) => {
          removeFromWishlist(productId);
          dispatchCTAClickObject('loccitane', 'button', 'wishList', 'removeProduct', '');
        }}
      />
      <PostCardMobile
        wishlistProducts={wishlistProducts}
        activeLocale={activeLocale}
        active={postCardMobileActive}
        setActive={() => {
          setPostCardMobileActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'postCard', 'close', '');
        }}
        removeFromWishlist={(productId: string) => removeFromWishlist(productId)}
      />
      <PostCardSent
        activeLocale={activeLocale}
        active={postCardSentActive}
        setActive={() => setPostCardSentActive(false)}
      />
      <AddedToCart
        activeLocale={activeLocale}
        active={addedToCartActive}
        setActive={() => setAddedToCartActive(false)}
      />
      <Completed
        activeLocale={activeLocale}
        active={miniGameActive}
        setActive={() => {
          setMiniGameActive(false);
          dispatchCTAClickObject('loccitane', 'button', 'minigameReward', 'close', '');
        }}
      />
      <TransitionVideo
        room={transitionVideo.gotoRoom}
        videoURL={transitionVideo.videoURL}
        playing={transitionVideo.active}
        setPlaying={() => {
          setTransitionVideo({ ...transitionVideo, active: false });
        }}
      />
    </>
  );
}

export default App;
